/**
 * 後台 — 帳號 / 變更密碼
 *
 * 簡易實作:輸入舊密碼 + 新密碼 + 確認新密碼,呼叫 /api/admin/change-password。
 * 改成功不必重 login(JWT email 沒變),只 toast 提示。
 */
import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminHeader from "../../components/AdminHeader";
import { adminChangePassword } from "../../api/admin";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";


export default function AdminAccount() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const email = useAuthStore((s) => s.email);
  const showToast = useUIStore((s) => s.showToast);

  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
    }
  }, [role, navigate]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!oldPw || !newPw || !confirmPw) {
      showToast("請完整填寫", "error");
      return;
    }
    if (newPw.length < 8) {
      showToast("新密碼至少 8 字", "error");
      return;
    }
    if (newPw !== confirmPw) {
      showToast("兩次新密碼不一致", "error");
      return;
    }
    if (newPw === oldPw) {
      showToast("新密碼與舊密碼相同,請更換", "error");
      return;
    }

    setBusy(true);
    try {
      const r = await adminChangePassword(oldPw, newPw);
      if (r.status === "ok") {
        showToast("密碼已更新", "success");
        setOldPw("");
        setNewPw("");
        setConfirmPw("");
      } else if (r.status === "wrong_old_password") {
        showToast("舊密碼不正確", "error");
      } else if (r.status === "password_too_short") {
        showToast("新密碼至少 8 字", "error");
      } else {
        showToast(`更新未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream">
      <AdminHeader active="account" />

      <div className="px-6 py-8 max-w-md">
        <div className="card space-y-4">
          <div>
            <div className="text-sm text-muted">登入 Email</div>
            <div className="text-base font-medium mt-1">{email || "—"}</div>
          </div>

          <div className="border-t border-line pt-4">
            <h2 className="font-bold text-ink mb-3">變更密碼</h2>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="label" htmlFor="acc-old">目前密碼</label>
                <input
                  id="acc-old"
                  type="password"
                  className="input"
                  value={oldPw}
                  onChange={(e) => setOldPw(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
              <div>
                <label className="label" htmlFor="acc-new">新密碼(至少 8 字)</label>
                <input
                  id="acc-new"
                  type="password"
                  className="input"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </div>
              <div>
                <label className="label" htmlFor="acc-confirm">確認新密碼</label>
                <input
                  id="acc-confirm"
                  type="password"
                  className="input"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              <button type="submit" disabled={busy} className="btn-primary w-full mt-2">
                {busy ? "更新中…" : "更新密碼"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
